import React, { useState, useEffect, useCallback } from "react";
import { caApi } from "../../utils/caApi";
import { useProfile } from "../context/ProfileContext";
import Header from "../header/AppHeader";
import ProfileCard from "../profile/ProfileCard";
import background from "../../assets/dashboard-bg.jpg";

const DAY_MS = 86400000;

const SUBLINE = {
  approved: "Approved · points credited",
  submitted: "Submitted, awaiting admin",
  rejected: "Rejected, needs resubmission",
  pending: "Pending submission",
};

/* ---------- helpers ---------- */

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString("default", { month: "short", day: "numeric" })
    : null;

const isReferralTask = (task) => task?.taskType === "Registration";

const checklistFor = (task) => {
  if (task?.checklistSteps) {
    return task.checklistSteps
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
  }
  return (task?.description || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => /^([-*•]|\[\s?\])\s+/.test(l))
    .map((l) => l.replace(/^([-*•]|\[\s?\])\s+/, ""));
};

const referralTarget = (task) => {
  const n = parseInt((task?.header?.match(/\d+/) || [])[0], 10);
  return Number.isFinite(n) && n > 0 ? n : 5;
};

const taskBadge = (task, referralCount = 0) => {
  const s = task.submissionStatus;
  if (s === "approved")
    return { label: "APPROVED", cls: "bg-green-100 text-green-700" };
  if (s === "submitted")
    return { label: "IN REVIEW", cls: "bg-black/[0.06] text-black/50" };
  if (s === "rejected")
    return { label: "REJECTED", cls: "bg-red-100 text-red-600" };

  if (isReferralTask(task)) {
    const done = referralCount >= referralTarget(task);
    return done
      ? { label: "READY", cls: "bg-green-100 text-green-700" }
      : { label: "ON TRACK", cls: "bg-green-100 text-green-700" };
  }

  if (task.deadline) {
    const diffDays = Math.ceil((new Date(task.deadline) - new Date()) / DAY_MS);
    if (diffDays < 0)
      return { label: "OVERDUE", cls: "bg-red-100 text-red-600" };
    if (diffDays === 0)
      return { label: "DUE TODAY", cls: "bg-[#D15B28] text-white" };
    if (diffDays === 1)
      return { label: "1 DAY LEFT", cls: "bg-black/[0.06] text-black/50" };
    return {
      label: `DUE IN ${diffDays} DAYS`,
      cls: "bg-black/[0.06] text-black/50",
    };
  }
  return { label: "OPEN", cls: "bg-black/[0.06] text-black/50" };
};

const actionLabel = (task) => {
  if (task.submissionStatus === "rejected") return "Resubmit →";
  if (isReferralTask(task)) return "View progress →";
  return "Submit Proof →";
};

/* ---------- shared UI primitives ---------- */

const ModalShell = ({ children, onClose, size = "max-w-xl" }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm font-montserrat"
    onClick={onClose}
  >
    <div
      className={`bg-[#FBF8F5] rounded-2xl shadow-2xl w-full ${size} max-h-[90vh] overflow-y-auto p-6 md:p-7 font-montserrat`}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  </div>
);

const CloseButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 transition flex items-center justify-center text-black/50 text-lg flex-shrink-0 font-montserrat"
    aria-label="Close"
  >
    ✕
  </button>
);

const ModalHead = ({ task, referralCount, onClose }) => {
  const rejected = task.submissionStatus === "rejected";
  const badge = taskBadge(task, referralCount);
  const due = fmtDate(task.deadline);
  return (
    <>
      <div className="flex justify-between items-start font-montserrat">
        <div>
          <p className="text-[11px] font-black tracking-widest uppercase text-[#D15B28] font-montserrat">
            {rejected
              ? "Action Needed · Resubmit"
              : isReferralTask(task)
                ? "Your Progress"
                : "Submit Proof"}
          </p>
          <h2 className="text-2xl font-black text-[#2B1B17] mt-1 font-montserrat">
            {task.header}
            {rejected && (
              <span className="text-black/40 font-montserrat"> (Resubmit)</span>
            )}
          </h2>
          <div className="flex items-center gap-3 mt-2 font-montserrat">
            <span
              className={`text-[10px] font-extrabold tracking-wider px-2.5 py-1 rounded-md uppercase font-montserrat ${badge.cls}`}
            >
              {badge.label}
            </span>
            <span className="text-sm text-black/50 font-medium font-montserrat">
              +{task.points ?? 0} pts{due && ` · Due ${due}`}
            </span>
          </div>
        </div>
        <CloseButton onClick={onClose} />
      </div>

      {rejected && task.reviewNotes && (
        <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 font-montserrat">
          <p className="text-[11px] font-extrabold tracking-widest text-red-600 uppercase font-montserrat">
            ⚠ Your submission was rejected
          </p>
          <p className="text-sm text-red-700/90 leading-relaxed mt-1.5 font-montserrat">
            {task.reviewNotes}
          </p>
        </div>
      )}
    </>
  );
};

const ProofModal = ({ task, referralCount, submitting, onClose, onSubmit }) => {
  const [link, setLink] = useState("");
  const [notes, setNotes] = useState("");
  const wantsLink = task.taskType === "Social Media" || !task.taskType;

  return (
    <ModalShell onClose={onClose}>
      <ModalHead task={task} referralCount={referralCount} onClose={onClose} />
      {task.description && (
        <div className="mt-5 pt-5 border-t border-black/5 font-montserrat">
          <p className="text-[11px] font-bold tracking-widest text-[#2B1B17]/40 uppercase font-montserrat">
            Task Description
          </p>
          <p className="text-sm text-black/60 leading-relaxed mt-1.5 whitespace-pre-line font-montserrat">
            {task.description}
          </p>
        </div>
      )}
      {wantsLink && (
        <div className="mt-5 font-montserrat">
          <label className="text-[11px] font-bold tracking-widest text-[#2B1B17]/40 uppercase font-montserrat">
            Proof Link
          </label>
          <input
            type="text"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://www.instagram.com/reel/..."
            className="w-full mt-1.5 bg-white border border-black/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#D15B28] transition font-montserrat"
          />
        </div>
      )}
      <div className="mt-5 font-montserrat">
        <label className="text-[11px] font-bold tracking-widest text-[#2B1B17]/40 uppercase font-montserrat">
          Additional Notes{" "}
          <span className="text-black/30 normal-case font-medium font-montserrat">
            (optional)
          </span>
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Add any extra context for the coordinator..."
          className="w-full mt-1.5 bg-white border border-black/10 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:border-[#D15B28] transition font-montserrat"
        />
      </div>
      <div className="mt-6 flex items-center gap-3 font-montserrat">
        <button
          onClick={() =>
            onSubmit({ proofLink: link.trim(), proofText: notes.trim() })
          }
          disabled={submitting}
          className="flex-1 bg-[#D15B28] text-white font-semibold py-3.5 rounded-full hover:bg-[#b04a1f] transition disabled:opacity-60 font-montserrat"
        >
          {submitting ? "Submitting..." : "Submit Proof ➜"}
        </button>
        <button
          onClick={onClose}
          className="px-7 py-3.5 rounded-full bg-black/5 hover:bg-black/10 transition font-semibold text-[#2B1B17] font-montserrat"
        >
          Cancel
        </button>
      </div>
    </ModalShell>
  );
};

const ChecklistModal = ({
  task,
  referralCount,
  items,
  submitting,
  onClose,
  onSubmit,
}) => {
  const [checked, setChecked] = useState(() => items.map(() => false));
  const [notes, setNotes] = useState("");
  const allDone = checked.every(Boolean);
  const toggle = (i) =>
    setChecked((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  return (
    <ModalShell onClose={onClose}>
      <ModalHead task={task} referralCount={referralCount} onClose={onClose} />
      <div className="mt-5 pt-5 border-t border-black/5 font-montserrat">
        <p className="text-[11px] font-bold tracking-widest text-[#2B1B17]/40 uppercase font-montserrat">
          Confirm Each Step
        </p>
        <div className="mt-3 flex flex-col gap-2.5 font-montserrat">
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              onClick={() => toggle(i)}
              className={`flex items-center gap-3 text-left rounded-xl border px-4 py-3 transition font-montserrat ${
                checked[i]
                  ? "border-[#D15B28]/40 bg-[#D15B28]/[0.06]"
                  : "border-black/10 bg-white hover:border-[#D15B28]/30"
              }`}
            >
              <span
                className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 text-[11px] font-black transition font-montserrat ${
                  checked[i]
                    ? "bg-[#D15B28] text-white"
                    : "bg-black/5 text-transparent"
                }`}
              >
                ✓
              </span>
              <span className="text-sm font-medium text-[#2B1B17] font-montserrat">
                {item}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="mt-5 font-montserrat">
        <label className="text-[11px] font-bold tracking-widest text-[#2B1B17]/40 uppercase font-montserrat">
          Additional Notes{" "}
          <span className="text-black/30 normal-case font-medium font-montserrat">
            (optional)
          </span>
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Add any extra context for the coordinator..."
          className="w-full mt-1.5 bg-white border border-black/10 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:border-[#D15B28] transition font-montserrat"
        />
      </div>
      <div className="mt-6 flex items-center gap-3 font-montserrat">
        <button
          onClick={() =>
            onSubmit({
              proofText:
                "Checklist completed:\n" +
                items.map((i) => `✓ ${i}`).join("\n") +
                (notes.trim() ? `\n\nNotes: ${notes.trim()}` : ""),
            })
          }
          disabled={submitting || !allDone}
          className="flex-1 bg-[#D15B28] text-white font-semibold py-3.5 rounded-full hover:bg-[#b04a1f] transition disabled:opacity-50 font-montserrat"
        >
          {submitting ? "Submitting..." : "Submit Proof ➜"}
        </button>
        <button
          onClick={onClose}
          className="px-7 py-3.5 rounded-full bg-black/5 hover:bg-black/10 transition font-semibold text-[#2B1B17] font-montserrat"
        >
          Cancel
        </button>
      </div>
    </ModalShell>
  );
};

const ReferralModal = ({ task, submitting, onClose, onSubmit }) => {
  const [notes, setNotes] = useState("");

  return (
    <ModalShell onClose={onClose}>
      <ModalHead 
        task={task} 
        referralCount={0} 
        onClose={onClose} 
      />

      {task.description && (
        <div className="mt-5 pt-5 border-t border-black/5 font-montserrat">
          <p className="text-[11px] font-bold tracking-widest text-[#2B1B17]/40 uppercase font-montserrat">
            Task Description
          </p>

          <p className="text-sm text-black/60 leading-relaxed mt-1.5 whitespace-pre-line font-montserrat">
            {task.description}
          </p>
        </div>
      )}

      <div className="mt-5 font-montserrat">
        <label className="text-[11px] font-bold tracking-widest text-[#2B1B17]/40 uppercase font-montserrat">
          Your Notes
        </label>

        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={5}
          placeholder="Write your registration details, completion notes, feedback, or any required information..."
          className="
            w-full mt-1.5 
            bg-white border border-black/10 
            rounded-xl px-4 py-3 
            text-sm resize-none
            focus:outline-none focus:border-[#D15B28]
            transition font-montserrat
          "
        />
      </div>

      <div className="mt-6 flex items-center gap-3 font-montserrat">
        <button
          onClick={() =>
            onSubmit({
              proofText: notes.trim(),
            })
          }
          disabled={submitting || !notes.trim()}
          className="
            flex-1 
            bg-[#D15B28] 
            text-white 
            font-semibold 
            py-3.5 
            rounded-full 
            hover:bg-[#b04a1f] 
            transition 
            disabled:opacity-60 
            font-montserrat
          "
        >
          {submitting ? "Submitting..." : "Submit Notes ➜"}
        </button>

        <button
          onClick={onClose}
          className="
            px-7 py-3.5 
            rounded-full 
            bg-black/5 
            hover:bg-black/10 
            transition 
            font-semibold 
            text-[#2B1B17]
            font-montserrat
          "
        >
          Cancel
        </button>
      </div>
    </ModalShell>
  );
};

const SuccessModal = ({ task, onClose, onView }) => (
  <ModalShell onClose={onClose} size="max-w-md">
    <div className="text-center font-montserrat">
      <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 mx-auto flex items-center justify-center text-2xl font-montserrat">
        ✓
      </div>
      <h2 className="text-2xl font-black text-[#2B1B17] mt-4 font-montserrat">
        Submission received
      </h2>
      <p className="text-sm text-black/50 leading-relaxed mt-2 font-montserrat">
        Your proof for “{task.header}” is now with the admin team for review.
      </p>
    </div>
    <div className="mt-5 rounded-xl border border-black/10 bg-white p-4 flex items-center justify-between gap-3 font-montserrat">
      <div className="min-w-0 font-montserrat">
        <p className="font-bold text-[#2B1B17] truncate font-montserrat">
          {task.header}
        </p>
        <p className="text-xs text-black/40 mt-0.5 font-montserrat">
          +{task.points ?? 0} pts · awaiting review
        </p>
      </div>
      <span className="text-[10px] font-extrabold tracking-wider px-2.5 py-1 rounded-md uppercase bg-[#D15B28] text-white flex-shrink-0 font-montserrat">
        Pending Review
      </span>
    </div>
    <p className="text-xs text-black/40 text-center mt-3 font-montserrat">
      You'll get a notification the moment it's reviewed.
    </p>
    <button
      onClick={onClose}
      className="w-full mt-5 bg-[#D15B28] text-white font-semibold py-3.5 rounded-full hover:bg-[#b04a1f] transition font-montserrat"
    >
      Back to tasks
    </button>
    <button
      onClick={onView}
      className="w-full mt-2 text-sm font-bold text-black/40 hover:text-black/60 transition py-1 font-montserrat"
    >
      View my submission
    </button>
  </ModalShell>
);

const DeleteModal = ({ task, deleting, onClose, onConfirm }) => (
  <ModalShell onClose={onClose} size="max-w-md">
    <div className="text-center font-montserrat">
      <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 mx-auto flex items-center justify-center text-2xl font-montserrat">
        ⚠
      </div>
      <h2 className="text-2xl font-black text-[#2B1B17] mt-4 font-montserrat">
        Delete this submission?
      </h2>
      <p className="text-sm text-black/50 leading-relaxed mt-2 font-montserrat">
        Your proof for “{task.header}” will be permanently deleted and the task
        returns to Not submitted. You can submit again before the deadline.
      </p>
    </div>
    <div className="mt-6 flex items-center gap-3 font-montserrat">
      <button
        onClick={onConfirm}
        disabled={deleting}
        className="flex-1 bg-red-600 text-white font-semibold py-3.5 rounded-full hover:bg-red-700 transition disabled:opacity-60 font-montserrat"
      >
        {deleting ? "Deleting..." : "Delete submission"}
      </button>
      <button
        onClick={onClose}
        className="px-7 py-3.5 rounded-full bg-black/5 hover:bg-black/10 transition font-semibold text-[#2B1B17] font-montserrat"
      >
        Cancel
      </button>
    </div>
  </ModalShell>
);

const isUrl = (s) => /^https?:\/\//i.test((s || "").trim());

const ProofTextModal = ({ task, onClose }) => (
  <ModalShell onClose={onClose} size="max-w-md">
    <div className="flex justify-between items-start font-montserrat">
      <div>
        <p className="text-[11px] font-black tracking-widest uppercase text-[#D15B28] font-montserrat">
          Your Submission
        </p>
        <h2 className="text-2xl font-black text-[#2B1B17] mt-1 font-montserrat">
          {task.header}
        </h2>
      </div>
      <CloseButton onClose={onClose} />
    </div>
    <div className="mt-5 rounded-xl border border-black/10 bg-white p-4 font-montserrat">
      {task.proofLink && (
        <>
          <p className="text-[11px] font-bold tracking-widest text-[#2B1B17]/40 uppercase font-montserrat">
            Submitted link
          </p>
          <a
            href={
              isUrl(task.proofLink)
                ? task.proofLink.trim()
                : `https://${task.proofLink.trim()}`
            }
            target="_blank"
            rel="noreferrer"
            className="text-sm text-[#D15B28] font-semibold break-all hover:underline mt-1.5 inline-block font-montserrat"
          >
            {task.proofLink.trim()}
          </a>
        </>
      )}
      {task.proofText && (
        <div className={task.proofLink ? "mt-4" : ""}>
          <p className="text-[11px] font-bold tracking-widest text-[#2B1B17]/40 uppercase font-montserrat">
            Notes
          </p>
          <p className="text-sm text-black/70 leading-relaxed mt-1.5 whitespace-pre-line break-words font-montserrat">
            {task.proofText}
          </p>
        </div>
      )}
    </div>
    <button
      onClick={onClose}
      className="w-full mt-5 bg-[#D15B28] text-white font-semibold py-3.5 rounded-full hover:bg-[#b04a1f] transition font-montserrat"
    >
      Close
    </button>
  </ModalShell>
);

/* ---------- page ---------- */

const TaskSection = () => {
  const { tasks: cachedTasks, loadTasks, refresh, data } = useProfile();
  const tasks = cachedTasks || [];
  const referralCount = data?.referralCount ?? 0;

  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const [activeTask, setActiveTask] = useState(null);
  const [successTask, setSuccessTask] = useState(null);
  const [deleteTask, setDeleteTask] = useState(null);
  const [proofTextTask, setProofTextTask] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(() => {
    setError(null);
    loadTasks(true).catch(() =>
      setError("Unable to load your tasks. Please try again."),
    );
  }, [loadTasks]);

  useEffect(() => {
    loadTasks().catch(() =>
      setError("Unable to load your tasks. Please try again."),
    );
  }, [loadTasks]);

  const submitProof = async ({ proofLink = "", proofText = "" }) => {
    if (!proofLink && !proofText) {
      alert("Please add a proof link or write a note.");
      return;
    }
    setSubmitting(true);
    try {
      await caApi.post(`/task/${activeTask.id}/submit`, {
        proofLink,
        proofText,
      });
      const done = activeTask;
      setActiveTask(null);
      setSuccessTask(done);
      await Promise.all([loadTasks(true), refresh()]);
    } catch (err) {
      alert(err.response?.data?.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await caApi.delete(`/task/${deleteTask.id}/submission`);
      setDeleteTask(null);
      await Promise.all([loadTasks(true), refresh()]);
    } catch (err) {
      alert(err.response?.data?.message || "Could not delete submission");
    } finally {
      setDeleting(false);
    }
  };

  if (!cachedTasks && !error) {
    return (
      <div className="min-h-screen bg-[#0E0705] text-[#F8F5F2] flex items-center justify-center font-montserrat">
        <div className="text-lg opacity-70 animate-pulse font-montserrat">
          Loading your tasks...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0E0705] text-[#F8F5F2] flex flex-col items-center justify-center p-4 font-montserrat">
        <p className="text-red-400 mb-4 font-medium font-montserrat">{error}</p>
        <button
          onClick={fetchData}
          className="bg-[#D15B28] text-white px-6 py-2 rounded-full font-semibold transition hover:bg-[#b04a1f] font-montserrat"
        >
          Retry
        </button>
      </div>
    );
  }

  const pendingReview = tasks.filter(
    (t) => t.submissionStatus === "submitted",
  ).length;
  const visibleTasks =
    filter === "all"
      ? tasks
      : tasks.filter((t) => t.submissionStatus === filter);

  const checklistItems = activeTask ? checklistFor(activeTask) : [];
  const activeKind = activeTask
    ? isReferralTask(activeTask)
      ? "referral"
      : checklistItems.length >= 2
        ? "checklist"
        : "proof"
    : null;

  return (
    <div
      className="h-[100dvh] w-full overflow-y-auto overflow-x-hidden lg:overflow-hidden bg-center bg-cover bg-no-repeat text-[#2B1B17] font-montserrat flex flex-col antialiased"
      style={{ backgroundImage: `url(${background})` }}
    >
      <Header activeTab="Tasks" />

      {/* --- HERO SECTION --- */}
      <section className="px-6 md:px-12 pt-4 pb-3 max-w-[1600px] mx-auto w-full flex-shrink-0 font-montserrat">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 relative z-10 font-montserrat">
          <div className="text-white flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5 max-w-3xl font-montserrat">
            <h1 className="text-[44px] sm:text-[56px] md:text-[70px] font-barlow font-black tracking-tight drop-shadow-sm leading-none whitespace-nowrap">
              Your Tasks
            </h1>
            <p className="text-sm md:text-base text-white/90 leading-relaxed font-normal drop-shadow-sm sm:max-w-[40rem] font-montserrat">
              Every task the coordinators have uploaded for you, with deadlines,
              points, and submission status.
            </p>
          </div>

          <ProfileCard />
        </div>
      </section>

      {/* --- ALL TASKS PANEL --- */}
      <main className="max-w-[1600px] mx-auto px-2 md:px-2 w-full relative z-25 lg:flex-1 lg:min-h-0 pb-2 flex flex-col font-montserrat">
        <div className="bg-[#F5EFEB]/90 backdrop-blur-md rounded-2xl p-5 md:p-6 shadow-xl border border-white/20 flex flex-col lg:flex-1 lg:min-h-0 lg:overflow-hidden font-montserrat">
          <div className="flex justify-between items-center mb-3 pb-3 border-b border-black/5 flex-shrink-0 font-montserrat">
            <div>
              <h2 className="text-sm font-black tracking-widest text-[#2B1B17]/60 uppercase font-montserrat">
                All Tasks
              </h2>
              <p className="text-xs text-black/40 font-medium mt-0.5 font-montserrat">
                {tasks.length} {tasks.length === 1 ? "task" : "tasks"} ·{" "}
                {pendingReview} pending review
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs font-bold text-[#D15B28] font-montserrat">
              <span className="text-black/40 font-montserrat">Showing:</span>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="bg-transparent text-[#D15B28] font-bold cursor-pointer focus:outline-none font-montserrat"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="submitted">In Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          <div className="lg:overflow-y-auto lg:flex-1 lg:min-h-0 divide-y divide-black/[0.04] font-montserrat">
            {visibleTasks.length === 0 && (
              <p className="text-sm text-black/40 py-10 text-center font-montserrat">
                No tasks to show.
              </p>
            )}
            {visibleTasks.map((task) => {
              const badge = taskBadge(task, referralCount);
              const overdue =
                task.deadline && new Date(task.deadline) < new Date();
              const actionable =
                task.submissionStatus === "pending" ||
                (task.submissionStatus === "rejected" && !overdue);
              return (
                <div
                  key={task.id}
                  className="flex flex-row items-center justify-between gap-4 py-3 font-montserrat"
                >
                  <div className="flex-1 min-w-0 font-montserrat">
                    <p className="font-bold text-[#2B1B17] truncate font-montserrat">
                      {task.header}
                    </p>
                    <p className="text-xs text-black/40 mt-0.5 font-montserrat">
                      {SUBLINE[task.submissionStatus] || "Pending submission"}
                    </p>
                  </div>

                  <div className="flex items-center flex-wrap gap-x-4 flex-shrink-0 font-montserrat">
                    <span className="font-black text-[#2B1B17] whitespace-nowrap w-20 text-right font-montserrat">
                      +{task.points ?? 0} pts
                    </span>

                    <span
                      className={`text-[10px] font-extrabold tracking-wider px-3 py-1.5 rounded-md uppercase w-28 text-center font-montserrat ${badge.cls}`}
                    >
                      {badge.label}
                    </span>

                    <div className="w-44 flex justify-end items-center gap-3 font-montserrat">
                      {actionable ? (
                        <button
                          onClick={() => setActiveTask(task)}
                          className="text-xs font-bold text-[#D15B28] bg-[#D15B28]/10 hover:bg-[#D15B28]/20 transition px-5 py-2.5 rounded-full whitespace-nowrap font-montserrat"
                        >
                          {actionLabel(task)}
                        </button>
                      ) : task.submissionStatus === "submitted" ? (
                        <>
                          {task.proofLink || task.proofText ? (
                            <button
                              onClick={() => setProofTextTask(task)}
                              className="text-xs font-bold text-black/40 hover:text-black/60 transition font-montserrat"
                            >
                              View
                            </button>
                          ) : null}
                          <button
                            onClick={() => setDeleteTask(task)}
                            className="text-xs font-bold text-red-500/80 hover:text-red-600 transition whitespace-nowrap font-montserrat"
                          >
                            Delete
                          </button>
                        </>
                      ) : task.proofLink || task.proofText ? (
                        <button
                          onClick={() => setProofTextTask(task)}
                          className="text-xs font-bold text-black/40 hover:text-black/60 transition px-2 font-montserrat"
                        >
                          View proof
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      {/* --- modals --- */}
      {activeTask && activeKind === "proof" && (
        <ProofModal
          task={activeTask}
          referralCount={referralCount}
          submitting={submitting}
          onClose={() => setActiveTask(null)}
          onSubmit={submitProof}
        />
      )}
      {activeTask && activeKind === "checklist" && (
        <ChecklistModal
          task={activeTask}
          referralCount={referralCount}
          items={checklistItems}
          submitting={submitting}
          onClose={() => setActiveTask(null)}
          onSubmit={submitProof}
        />
      )}
      {activeTask && activeKind === "referral" && (
        <ReferralModal
          task={activeTask}
          profile={data}
          submitting={submitting}
          onClose={() => setActiveTask(null)}
          onSubmit={submitProof}
        />
      )}

      {successTask && (
        <SuccessModal
          task={successTask}
          onClose={() => setSuccessTask(null)}
          onView={() => {
            setFilter("submitted");
            setSuccessTask(null);
          }}
        />
      )}

      {deleteTask && (
        <DeleteModal
          task={deleteTask}
          deleting={deleting}
          onClose={() => setDeleteTask(null)}
          onConfirm={confirmDelete}
        />
      )}

      {proofTextTask && (
        <ProofTextModal
          task={proofTextTask}
          onClose={() => setProofTextTask(null)}
        />
      )}
    </div>
  );
};

export default TaskSection;